import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import { AppConfig, Config, Partner, VERSION_TOKEN } from './app/app.config';
import packageJson from 'package-lock.json';
import { AUTH_CONFIG_TOKEN } from './app/auth/auth-config.module';
import { AUTH_INTERCEPTOR_CONFIG_TOKEN } from './app/auth/interceptor-config.model';

if (environment.production) {
  enableProdMode();
}

const getUrlPartnerName = (): string => {
  const globalPartnerFromUrl = window.location.href
    .split('/')
    .filter(e => !!e)
    .pop() as string;

  let partnerFromUrlCreateAccount = '';
  if (window.location.href.includes('/#/create-account?')) {
    const match = window.location.href.match(/partner=(?<partner>[^&]+)&username=(?<username>[^&]+)$/);
    if (match) {
      partnerFromUrlCreateAccount = (match.groups as { partner: string; username: string }).partner;
    }
  }

  const partnerUrlFromStorage = sessionStorage.getItem('ecm-partner-name');

  return partnerUrlFromStorage || partnerFromUrlCreateAccount || globalPartnerFromUrl;
};

const loadConfig = (): Promise<void | Config> => {
  const configUrl = { url: 'assets/config.json', errorMessage: 'error while parsing config' };
  const partnerUrl = { url: 'partners/search', errorMessage: 'error while parsing partner' };

  return fetch(configUrl.url)
    .catch(err => console.error(configUrl.errorMessage, err))
    .then(result => result?.json())
    .then(config => {
      return Promise.all([
        config,
        fetch(`${config.url}/${partnerUrl.url}`, {
          method: 'POST',
          headers: {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ queryParameter: getUrlPartnerName() })
        })
          .catch(err => console.error(partnerUrl.errorMessage, err))
          .then(result => result?.json())
      ]).then(
        ([config, partner]) =>
          ({
            ...config,
            ...partner
          } as Config)
      );
    });
};

const manageUserCreation = (config: Config, partner: string, username: string): Promise<void> => {
  const activateAccountUrl = {
    url: `api/account/activate?partner=${partner}&username=${username}`,
    errorMessage: 'error while activating'
  };

  return fetch(`${config.url}/${activateAccountUrl.url}`)
    .catch(err => console.error(activateAccountUrl.errorMessage, err))
    .then();
};

loadConfig().then(config => {
  if (!config) {
    console.error('Config has not been loaded');
    return;
  }

  if (window.location.href.includes('/#/create-account?')) {
    const match = window.location.href.match(/partner=(?<partner>[^&]+)&username=(?<username>[^&]+)$/);
    if (match) {
      const { partner, username } = match.groups as { partner: string; username: string };
      manageUserCreation(config, partner, username).then(() => {
        window.location.replace(
          `${window.location.href.split('#')[0]}create-account.html?partnerId=${
            config.partner?.pingName
          }&partner=${partner}`
        );
      });
    } else {
      console.error('404 Resource not found');
    }
    return;
  }

  const partner = getPartnerFromUrl(config.partner as Partner['partner']);

  platformBrowserDynamic([
    {
      provide: AppConfig,
      useValue: {
        baseWsURL: (): string => config.url,
        ssoUrl: (): string => config.ssoBaseUrl,
        cookieSecret: (): string => config.cookieSecret
      }
    },
    {
      provide: AUTH_CONFIG_TOKEN,
      useValue: { ...config.ssoConfig, partner }
    },
    {
      provide: AUTH_INTERCEPTOR_CONFIG_TOKEN,
      useValue: config.interceptor
    },
    {
      provide: VERSION_TOKEN,
      useValue: packageJson.version
    }
  ])
    .bootstrapModule(AppModule, { preserveWhitespaces: true })
    .catch(err => console.log(err));
});

const getPartnerFromUrl = ((): ((partnerConfig: Partner['partner']) => string) => {
  function isAuthenticating(partnerFromUrl: string): boolean {
    return ['code=', 'state='].every(str => partnerFromUrl.includes(str));
  }

  function isAuthenticated(): boolean {
    const storage = localStorage.getItem('0-ecm');
    const accessToken = storage ? JSON.parse(storage)['access_token_expires_at'] : null;
    return !!storage && new Date(accessToken) > new Date();
  }

  function onCookiePage(): boolean {
    return window.location.href.endsWith('/#/privacy-cookies');
  }

  function manageNewPartnerAsked(): void {
    const wantedPartner = sessionStorage.getItem('ecm-wanted-partner-name');
    if (wantedPartner) {
      sessionStorage.removeItem('ecm-wanted-partner-name');
      sessionStorage.setItem('ecm-partner-name', wantedPartner);
    }
  }

  function manageLoggedInPartner(partnerFromUrl: string, partner: string): string {
    const storage = localStorage.getItem('0-ecm');
    if (storage) {
      const userData = JSON.parse(storage)['userData'];
      if (userData) {
        const loggedInPartner = userData['partner_name'];
        if (partner !== loggedInPartner) {
          if (partner && partner !== 'logout') {
            sessionStorage.setItem('ecm-wanted-partner-name', partnerFromUrl || partner || '');
            sessionStorage.setItem('ecm-wanted-sso-partner-name', partner || '');
            return 'logout';
          } else {
            return loggedInPartner;
          }
        }
      }
    }
    return partnerFromUrl;
  }

  function manageAccessDenied(): boolean {
    const isDenied = window.location.href.includes('error=access_denied');
    if (isDenied) {
      sessionStorage.setItem('ecm-partner-name', 'denied');
    }
    return isDenied;
  }

  return (partnerConfig: Partner['partner']): string => {
    let partnerFromUrl = getUrlPartnerName();
    const partner = partnerConfig.pingName;

    manageNewPartnerAsked();
    partnerFromUrl = manageLoggedInPartner(partnerFromUrl, partner);
    const isDenied = manageAccessDenied();

    if (!(partner || isAuthenticating(partnerFromUrl) || isAuthenticated() || onCookiePage() || isDenied)) {
      throw new Error('URL not found');
    }
    if (partner) {
      sessionStorage.setItem('ecm-partner-theme-name', partner);
      sessionStorage.setItem('ecm-partner-name', partnerFromUrl);
    }
    return partner || '';
  };
})();
